import {
  IonList,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonInput,
  IonButton,
  IonIcon,
  useIonViewWillEnter,
  IonCheckbox,
} from "@ionic/react";
import { chevronDownOutline, searchOutline, searchSharp } from "ionicons/icons";
import CriteriaModal from "./CriteriaModal";
import {
  CriteriaDef,
  CriteriaParameter,
  FilterParam,
  QueryInfo,
} from "../hooks/useQueryExec";
import { useContext, useEffect, useState } from "react";
import { TranslationsContext } from "../util/Translations";
import { getUserSetupValues } from "../api/UserSetups";
import FilterModal from "./FilterModal";
import { ApiResponse } from "../models/api/ApiResponse";
import { getValueSearchDetails } from "../api/QueryDesigner";
import { getUserId } from "../util/UserHelper";
import QueryInfoPopover from "./QueryInfoPopover";

export interface QueryFilterParams {
  setShowLoading: (arg0: boolean) => void;
  getQueryList: () => void;
  queryList: string[];
  querySection: string;
  getFilterParams: () => void;
  filterParams: FilterParam[];
  setParameter: (
    paramKey: string,
    paramValue: string | number | null | undefined
  ) => void;
  queryCriteria: CriteriaDef[];
  getQueryCriteria: (queryName: string) => void;
  searchCallback: (
    selectedQuery: string,
    filterParams?: FilterParam[],
    criteriaParams?: CriteriaParameter[]
  ) => void;
  filterModalInvalid?: boolean;
  queryInfo?: QueryInfo;
  triggerId?: string;
  // Bug 916751: opt-in. When set, filter params whose value search resolves to a
  // finite value list with NO query-wizard module (e.g. buyer -> sy_buyer via
  // custom_sql) render as an inline dropdown instead of a free-text field.
  // Module-backed value searches (supplier, PO #) keep their search-modal icon.
  enableInlineDropdowns?: boolean;
  // Opt-in. When set, an already-selected query is kept on view re-entry instead
  // of being reset to the saved default. Needed for the PO approval flow, where
  // approving a PO routes to a detail page and back; without this the query the
  // user ran is wiped (reset to the empty startup default) on return.
  preserveQueryOnReenter?: boolean;
}

const QueryFilter: React.FC<QueryFilterParams> = (props) => {
  const { translations } = useContext(TranslationsContext);
  const {
    setShowLoading,
    getQueryList,
    queryList,
    querySection,
    getFilterParams,
    filterParams,
    setParameter,
    queryCriteria,
    getQueryCriteria,
    searchCallback,
    filterModalInvalid,
    queryInfo,
    triggerId,
    enableInlineDropdowns,
    preserveQueryOnReenter,
  } = props;
  let [loggedInEmployee, setLoggedInEmployee] = useState<string>("");
  const [currentQuery, setCurrentQuery] = useState<string>("");
  const [isCriteriaModalOpen, setIsCriteriaModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterModalColumnName, setFilterModalColumnName] = useState("");
  const [filterModalModuleName, setFilterModalModuleName] = useState("");
  const startup = "startup query";
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [filterModalStack, setFilterModalStack] = useState<
    { columnName: string; moduleName: string }[]
  >([]);
  const topModal = filterModalStack[filterModalStack.length - 1];

  // Bug 916751: inline-dropdown value lists, keyed by filter column name. Only
  // populated when enableInlineDropdowns is set and the value search returns a
  // finite list without a query-wizard module (custom_sql lists like sy_buyer).
  const [inlineDropdowns, setInlineDropdowns] = useState<{
    [columnName: string]: { Code: string; Description: string }[];
  }>({});
  // Local echo of the selected value so the dropdown reflects the choice even
  // though setParameter mutates the shared filterParams array in place.
  const [dropdownSelection, setDropdownSelection] = useState<{
    [columnName: string]: string;
  }>({});

  useEffect(() => {
    if (!enableInlineDropdowns || !filterParams || filterParams.length === 0) {
      return;
    }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        filterParams.map(async (p) => {
          try {
            const res: ApiResponse = await getValueSearchDetails({
              tableName: p.TableName,
              columnName: p.ColumnName,
            });
            const data = res.data?.Data;
            const hasModule = !!res.data?.QueryWizardModuleName;
            if (!hasModule && Array.isArray(data) && data.length > 0) {
              return { column: p.ColumnName, values: data };
            }
          } catch {
            // leave as a text field if the value search can't be resolved
          }
          return null;
        })
      );
      if (cancelled) return;
      const map: {
        [columnName: string]: { Code: string; Description: string }[];
      } = {};
      entries.forEach((e) => {
        if (e) map[e.column] = e.values;
      });
      setInlineDropdowns(map);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams, enableInlineDropdowns]);

  useEffect(() => {
    setShowLoading(true);
    Promise.all([getEmployee(), getFilterParams(), getQueryList()]).finally(
      () => {
        getDefaultQuery();
        setIsInitialLoadComplete(true);
      }
    );
  }, []);

  const closeFilterModal = () => {
    setFilterModalStack((prev) => prev.slice(0, -1));
  };

  const openFilterModal = (columnName: string, moduleName: string) => {
    setFilterModalStack((prev) => [...prev, { columnName, moduleName }]);
  };

  useEffect(() => {
    if (currentQuery) {
      getQueryCriteria(currentQuery);
    }
  }, [isInitialLoadComplete, currentQuery]);

  useIonViewWillEnter(() => {
    // Preserve an existing selection on re-entry when opted in (PO approval flow);
    // otherwise keep the original behavior of restoring the saved default query.
    if (!preserveQueryOnReenter || !currentQuery) {
      getDefaultQuery();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preserveQueryOnReenter, currentQuery]);

  const getEmployee = async () => {
    return await getUserId().then((response) => {
      if (response !== undefined) {
        setLoggedInEmployee(response!);
      }
    });
  };

  const getDefaultQuery = async () => {
    const data = {
      Section: querySection,
      ParameterList: [startup],
    };
    getUserSetupValues(data).then((response) => {
      if (response.data && response.data[startup]?.includes("--")) {
        let query = response.data[startup].split("--");
        setCurrentQuery(query[1]);
      } else {
        setCurrentQuery(response.data[startup]);
      }
    });
    setShowLoading(false);
  };

  const onSearch = () => {
    if (queryCriteria.length > 0) {
      setIsCriteriaModalOpen(true);
    } else {
      setShowLoading(true);
      searchCallback(currentQuery, filterParams);
    }
  };

  const onCriteriaModalCancel = () => {
    setIsCriteriaModalOpen(false);
  };

  const onCriteriaModalConfirm = (parameters: CriteriaParameter[]) => {
    setIsCriteriaModalOpen(false);
    searchCallback(currentQuery, filterParams, parameters);
  };

  const onFilterModalCancel = () => {
    setIsFilterModalOpen(false);
    setFilterModalColumnName("");
    setFilterModalModuleName("");
  };

  const onFilterModalConfirm = (selectedItem: string) => {
    setParameter(filterModalColumnName, selectedItem);
    setIsFilterModalOpen(false);
    setFilterModalColumnName("");
    setFilterModalModuleName("");
  };

  return (
    <>
      <CriteriaModal
        isOpen={isCriteriaModalOpen}
        cancel={onCriteriaModalCancel}
        confirm={onCriteriaModalConfirm}
        queryCriteria={queryCriteria}
        loggedInEmployee={loggedInEmployee}
      />
      {filterModalStack.length > 0 && topModal && (
        <FilterModal
          isOpen={true}
          moduleName={topModal.moduleName}
          cancel={closeFilterModal}
          confirm={(selectedItem: string) => {
            setParameter(topModal.columnName, selectedItem);
            closeFilterModal();
          }}
        />
      )}

      {filterModalInvalid ? (
        <></>
      ) : (
        <FilterModal
          isOpen={isFilterModalOpen}
          moduleName={filterModalModuleName}
          cancel={onFilterModalCancel}
          confirm={onFilterModalConfirm}
        />
      )}
      <IonList>
        <IonItem>
          <IonLabel position="floating">{`${translations["lbl_grp_query"] || "Query"
            }:`}</IonLabel>
          <IonSelect
            id="query-filter-select"
            interface="popover"
            placeholder={translations["lbl_btn_select"] || "Select"}
            value={currentQuery}
            onIonChange={(ev) => setCurrentQuery(ev.detail.value)}
          >
            {queryList.map((qryName, index) => {
              return (
                <IonSelectOption key={`${index}-${qryName}`}>
                  {qryName}
                </IonSelectOption>
              );
            })}
          </IonSelect>
        </IonItem>
        {filterParams.map((p) => {
          return (
            <IonItem key={p.ColumnName}>
              <IonLabel>{p.TranslatedIdText}</IonLabel>
              {p.ElementType === "checkBox" ? (
                <IonCheckbox
                  slot="end"
                  checked={p.ParameterValue === 1} // Default unchecked
                  onIonChange={(ev) =>
                    setParameter(p.ColumnName, ev.detail.checked ? 1 : 0)
                  }
                />
              ) : inlineDropdowns[p.ColumnName] ? (
                <IonSelect
                  slot="end"
                  interface="popover"
                  value={
                    dropdownSelection[p.ColumnName] ??
                    (p.ParameterValue as string) ??
                    ""
                  }
                  onIonChange={(ev) => {
                    setParameter(p.ColumnName, ev.detail.value);
                    setDropdownSelection((prev) => ({
                      ...prev,
                      [p.ColumnName]: ev.detail.value,
                    }));
                  }}
                >
                  {inlineDropdowns[p.ColumnName].map((v) => (
                    <IonSelectOption key={v.Code} value={v.Code}>
                      {v.Description ? `${v.Code} - ${v.Description}` : v.Code}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              ) : (
                <IonInput
                  class="ion-text-end"
                  value={p.ParameterValue}
                  onIonInput={(ev) =>
                    setParameter(p.ColumnName, ev.target.value)
                  }
                  maxlength={p.MaxSize}
                ></IonInput>
              )}
              {p.Requirements.length > 0 &&
                !!p.Requirements.find(
                  (value) => value.RequirementType === "Searchable"
                ) && (
                  <IonIcon
                    slot="end"
                    ios={searchOutline}
                    md={searchSharp}
                    onClick={async () => {
                      setFilterModalColumnName(p.ColumnName);
                      const response: ApiResponse = await getValueSearchDetails(
                        {
                          tableName: p.TableName,
                          columnName: p.ColumnName,
                        }
                      );
                      openFilterModal(p.ColumnName, response.data.QueryWizardModuleName);
                    }}
                  ></IonIcon>
                )}
            </IonItem>
          );
        })}
        <div style={{ display: "flex", gap: "2px" }}>
          <IonButton
            id="query-filter-search-button"
            disabled={!currentQuery}
            expand="full"
            color="primary"
            onClick={() => {
              onSearch();
            }}
            style={{ flex: 1 }}
          >
            {translations["lbl_btn_search"] || "Search"}
          </IonButton>
          {queryInfo && (
            <IonButton id={triggerId} expand="full">
              <IonIcon slot="icon-only" icon={chevronDownOutline}></IonIcon>
            </IonButton>
          )}
        </div>

        <QueryInfoPopover
          queryInfo={queryInfo}
          translations={translations}
          triggerId={triggerId}
        />
      </IonList>
    </>
  );
};

export default QueryFilter;
